import { useAppNavigate } from "hooks/useAppNavigate";
import React, { useState } from "react";
import Box from "@mui/material/Box";
import LoadingButton from "components/LoadingButton";
import OtpInput from "components/Otp";
import ErrorMessage from "components/ErrorMessage";
import PageTitle from "components/PageTitle";
import PageMeta from "components/PageMeta";
import JS2Native from "@applicaster/quick-brick-js-2-native";
import { approveDeviceCode, validateUserCode } from "services/auth.service";
import { useLoginPage } from "hooks/useLoginPage";
import { useText } from "context/TextProvider";

const LoginWithMobile = (): JSX.Element => {
  const text = useText();
  const appNavigate = useAppNavigate();
  const [userCode, setUserCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const js2NativeData = JS2Native?.();
  const { loginPageConfig } = useLoginPage();

  // Any failure here — bridge unavailable, storage read throwing — is
  // surfaced by the caller's single friendly message (accessTokenFailed);
  // this function only needs to log the real cause for debugging.
  const getAccessToken = async () => {
    try {
      const localStorage = js2NativeData?.localStorage;
      if (!localStorage) {
        console.error("[LoginWithMobile] localStorage bridge unavailable");
        return null;
      }
      const token = (await localStorage.getItem("access_token", "quick-brick-login-flow")) || "";
      return token.value;
    } catch (err) {
      console.error("[LoginWithMobile] localStorage.getItem failed:", err);
      return null;
    }
  };

  const handleValidate = async () => {
    setError("");
    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError(text.loginWithMobile.accessTokenFailed);
        setLoading(false);
        return;
      }
      await validateUserCode({
        user_code: userCode,
        url: loginPageConfig?.urls?.validate_user_code,
      });
      await approveDeviceCode({
        user_code: userCode,
        access_token: accessToken,
        url: loginPageConfig?.urls?.approve_device_code,
      });
      appNavigate(`/device-grant-flow/complete`);
    } catch {
      setError(text.loginWithMobile.invalidCode);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ textAlign: "center" }}>
          <PageMeta name="Login With Mobile" />
          <PageTitle>{text.loginWithMobile.title}</PageTitle>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <OtpInput otp={userCode} setOtp={setUserCode} acceptText />
          <LoadingButton
            loading={loading}
            onClick={handleValidate}
            sx={{
              width: "100%",
              height: "3.5rem",
            }}
          >
            {text.loginWithMobile.validateButton}
          </LoadingButton>
    </Box>
  );
};

export default LoginWithMobile;
